<!--
 For validation plugin documentation: https://github.com/baianat/vee-validate
 Include the following in your main.js file for this module to work properly:
   import VeeValidate from 'vee-validate'
   Vue.use(VeeValidate)
-->
<template>
  <div
    class="bh-input field-wrapper"
    :class="{ 'no-float-label': isPlaceholderPresent, 'highlight': highlight }"
  >
    <float-label :label="label" ref="floatLabel" class="float-label h100p" :on="!isPlaceholderPresent">
      <input
        class="w100p h100p"
        :data-vv-delay="dataVVDelay"
        :name="type"
        :type="type"
        :placeholder="label || placeholder"
        :autocomplete="autocomplete"
        :disabled="isDisabled"
        :required="required"
        :step="step"
        :min="inputMin"
        :max="max"
        :class="`${hasError && !turnOffValidation ? 'error' : ''} ${inputClasses}`"
        :value="value"
        @keydown="onKeyDown"
        @keyup="onKeyUp"
        @blur="onBlur"
        @input="emitValue"
      />
      <slot></slot>
    </float-label>
  </div>
</template>
<script>
import FloatLabel from 'vue-float-label/components/FloatLabel'

export default {
  name: 'bh-input',
  components: {
    FloatLabel
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    // if you do not want a float label,
    // pass label in as a placeholder instead of a label
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: String | Number
    },
    autocomplete: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputClasses: {
      type: String,
      default: 'f2'
    },
    required: {
      type: Boolean,
      default: false
    },
    /* HTML 5 step property:  https://www.w3schools.com/tags/att_input_step.asp */
    step: {
      type: String,
      /* use '0.01', for example, to allow decimal numbers */
      default: '1'
    },
    /* USED IN V-VALIDATE DIRECTIVE */
    /* min:{length} - http://vee-validate.logaretm.com/rules.html#rule-min */
    min: {
      type: Number
    },
    /* max:{length} - http://vee-validate.logaretm.com/rules.html#rule-max */
    max: {
      type: Number
    },
    /* max_value:{value} - http://vee-validate.logaretm.com/rules.html#rule-max_value */
    max_value: {
      type: Number,
      default: 1000000000
    },
    min_value: {
      type: Number,
      default: -1000000000
    },
    /* regex:{pattern} - http://vee-validate.logaretm.com/rules.html#rule-regex */
    regex: {
      type: RegExp
    },
    /* date_format:{format} - http://vee-validate.logaretm.com/rules.html#rule-date_format */
    date_format: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    dataVVDelay: {
      type: String,
      default: '200'
    },
    highlight: {
      type: Boolean
    },
    toFixed: {
      type: Boolean
    }
  },
  data: function () {
    return {
      initialValueIsSet: false
    }
  },
  watch: {
    value: {
      handler (value) {
        const enableFloatingLabelIf = value => {
          const isInputPopulated = value => {
            const isStringOrNum =
              typeof value === 'string' || typeof value === 'number'
            const isValueNotEmpty = isStringOrNum
              ? Boolean(String(value).trim())
              : false
            return isValueNotEmpty
          }
          return this.typeIsDateOrTime || isInputPopulated(value)
        }
        if (this.$refs['floatLabel']) {
          this.$refs['floatLabel'].isActive = !this.isPlaceholderPresent
            ? enableFloatingLabelIf(value)
            : false
        }
      }
    }
  },
  computed: {
    isDisabled: {
      get() {
        return this.disabled
      }
    },
    shouldForceTrailingZeros: {
      get() {
        return this.toFixed && this.step !== 'any'
      }
    },
    isPlaceholderPresent: {
      get() {
        return Boolean(this.placeholder)
      }
    },
    typeIsDateOrTime: {
      get() {
        return this.type === 'time' || this.type === 'date'
      }
    },
    hasError: {
      get() {
        if (this.errors) return this.errors.has(this.type)
      }
    },
    errorMessage: {
      get() {
        if (this.errors) return this.errors.first(this.type)
      }
    },
    turnOffValidation: {
      get() {
        return this.type === 'time'
      }
    },
    inputMin: {
      get() {
        return this.min ? undefined : 0
      }
    }
  },
  methods: {
    /**
     * Emit the typed in value
     * @param event
     */
    emitValue(event) {
      let value = event.target.value
      if (!this.initialValueIsSet && this.shouldForceTrailingZeros) {
        this.forceTrailingZeros(value)
      } else {
        if (this.type === 'number') {
          value = parseInt(value)
        }
        this.$emit('output', value)
      }
      this.initialValueIsSet = true
    },
    /**
     * Emit an on blur event
     */
    onBlur(event) {
      if (this.shouldForceTrailingZeros) {
        this.forceTrailingZeros(event.target.value)
      }
      this.$emit('blur')
    },
    /**
     * Emit a key up event
     */
    onKeyUp(event) {
      this.$emit('keyup', event)
    },
    /**
     * Emit a key down event
     */
    onKeyDown(event) {
      const buttonPressed = event.which
      const isNotArrowKeys = buttonPressed !== 37 && buttonPressed !== 39
      const isNotBackspaceKey = buttonPressed !== 8
      const isNotDeleteKey = buttonPressed !== 46
      if (isNotArrowKeys && isNotDeleteKey && isNotBackspaceKey) {
        this.checkKeyPressValidation(event)
      }
      this.$emit('keydown', event)
    },
    /**
     */
    forceTrailingZeros(value) {
      if (this.shouldForceTrailingZeros && typeof value === 'string') {
        const desiredNumberOfDecimals = this.checkStepForMaxDecimals()
        if (desiredNumberOfDecimals && !isNaN(parseFloat(value))) {
          value = parseFloat(value).toFixed(desiredNumberOfDecimals)
        }
        this.$emit('output', value)
      }
    },
    /**
     * Get the number of max decimals based on the step property.
     *
     * EX: if step === '0.001', then the maxNumber of decimals would be 3
     */
    checkStepForMaxDecimals() {
      let maxNumberOfDecimals
      if (this.step.indexOf('.') >= 0) {
        maxNumberOfDecimals =
          this.step % 1 !== 0 ? this.step.toString().split('.')[1].length : 0
      }
      return maxNumberOfDecimals
    },
    /**
     * Validate the number the user has typed. Stop the user from typing anymore numbers if the
     * max decimal point has been reached.
     */
    checkKeyPressValidation(e) {
      if (this.toFixed) {
        // Ensure that the number value is only the specified number of decimal places.
        // If it has maxed the number of decimals given, restrict the user from typing any more decimals.
        const value = e.target.value
        const hasDecimals = value && value.indexOf('.') >= 0
        if (hasDecimals && value) {
          const numberOfDecimals = value.split('.')[1].length
          const maxNumberOfDecimals = this.checkStepForMaxDecimals()
          if (numberOfDecimals >= maxNumberOfDecimals) {
            // Don't let the user type the key they pressed
            e.preventDefault()
          }
        }
      }
    }
  },
  mounted() {
    if (this.$refs.floatLabel) {
      if (this.isPlaceholderPresent) this.$refs.floatLabel.isActive = false
      if (this.typeIsDateOrTime) this.$refs.floatLabel.isActive = true
    }
  }
}
</script>

<style scoped>
input {
  font-weight: normal;
  color: var(--sirocco);
  background: transparent;
  border: none;
}

input.error,
input:hover.error,
input:focus.error {
  box-shadow: none;
  border: 1px solid var(--hennessey);
}

input::placeholder {
  font-family: 'Axiroma Medium';
  font-size: 1.5rem;
  color: #B7B6B6;
  text-align: left;
}

.error-message.error {
  font-weight: normal;
  color: var(--hennessey);
  font-size: 0.875rem; /* 14px */
}

.highlight input {
  color: var(--amethyst);
  font-weight: var(--font-weight-bold);
}

</style>
